import { Button } from '@beek/design-system';
import { Typography } from 'beek-design-system';
import { useTranslations } from 'next-intl';
import styled from 'styled-components';

type ArticleSectionProps = {
  type?: string;
  data: {
    tag: string;
    url: string;
  }[];
};

type ButtonContainerProps = {
  marginRight?: boolean;
};

type ButtonsWrapperProps = {
  lastButton: boolean;
  firstButton: boolean;
  key: string;
};

type SectionContainerProps = {
  type?: string;
};

type Dictionary = {
  [key_type: string]: string;
};

const defaultProps = {
  type: 'landing',
};

const getMarginTop = (isDesktop: boolean, type?: string): string => {
  const marginTopSizes: Dictionary = {
    landing: isDesktop ? '12.5rem' : '5.063rem',
    listas: isDesktop ? '8rem' : '8rem',
    audioContent: isDesktop ? '12.5rem' : '5.063rem',
  };
  return type ? marginTopSizes[type] : '5.063rem';
};
const SectionContainer = styled.section<SectionContainerProps>`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: ${({ type }): string => getMarginTop(false, type)};
  @media ${({ theme }): string => theme.devices.laptop} {
    width: 57.25rem;
    margin-top: ${({ type }): string => getMarginTop(true, type)};
  }
`;

const TextContainer = styled.div`
  margin: 0 1rem 2rem 1rem;
  @media ${({ theme }): string => theme.devices.laptop} {
    margin: 0 0 2.875rem 0;
  }
`;

const ArticleContainer = styled.div`
  overflow: auto;
  display: flex;
  flex-direction: column;
  @media ${({ theme }): string => theme.devices.laptop} {
    flex-direction: row;
    overflow: none;
  }
`;

const ButtonsContainer = styled.div<ButtonContainerProps>`
  display: flex;
  &::after {
    content: '';
    min-width: 1rem;
  }
  @media ${({ theme }): string => theme.devices.laptop} {
    flex-direction: column;
    align-items: flex-start;
    margin-right: ${(props): string => (props.marginRight ? '3.125rem' : '0')};
    &::after {
      content: none;
      min-width: 0;
    }
  }
`;

const ButtonsWrapper = styled.div<ButtonsWrapperProps>`
  margin-bottom: ${(props): string => (props.lastButton ? '0rem' : '0.5rem')};
  margin-left: ${(props): string => (props.firstButton ? '1rem' : '0rem')};
  margin-right: ${(props): string => (props.lastButton ? '0rem' : '0.5rem')};
  @media ${({ theme }): string => theme.devices.laptop} {
    margin-bottom: ${(props): string => (props.lastButton ? '0rem' : '1.5rem')};
    margin-left: 0;
    margin-right: 0;
  }

  font-family: var(--primary-font);
  font-weight: 700;
  -webkit-box-pack: center;
  text-decoration: none;
  display: flex;
  align-items: flex-start;
  white-space: nowrap;
`;

const TitleText = styled(Typography)`
  margin-bottom: 0.25rem;
  @media ${({ theme }): string => theme.devices.laptop} {
    margin-bottom: 1rem;
  }
`;

const getTranslationsIndex = (type?: string): string => {
  const translationsDict: Dictionary = {
    landing: 'landing',
    listas: 'landing',
    audioContent: 'audioContent',
  };
  return type ? translationsDict[type] : 'default';
};

const getTitleTypography = (type?: string): string => {
  const typographiesTypes: Dictionary = {
    landing: 'headline2',
    listas: 'headline1',
    audioContent: 'headline3',
  };
  return type ? typographiesTypes[type] : 'headline2';
};

const getSubtitleTypography = (type?: string): string => {
  const typographiesTypes: Dictionary = {
    landing: 'body1',
    listas: 'headline3',
    audioContent: 'headline4',
  };
  return type ? typographiesTypes[type] : 'headline2';
};

const ArticleSection = ({ data, type }: ArticleSectionProps): JSX.Element => {
  const t = useTranslations(getTranslationsIndex(type));
  const buttonsGroup1 = data.slice(0, 5);
  const buttonsGroup2 = data.slice(5, 10);
  const buttonsGroup3 = data.slice(10, 15);
  const buttonsGroup4 = data.slice(15, 20);

  return (
    <SectionContainer type={type}>
      <TextContainer>
        <TitleText
          type={getTitleTypography(type)}
          colorVariant="gray100"
          data-cy="last-articles-title"
        >
          {`${t('article.title')}`}
        </TitleText>
        <Typography type={getSubtitleTypography(type)} colorVariant="gray70">
          {`${t('article.subtitle')}`}
        </Typography>
      </TextContainer>
      <ArticleContainer>
        <ButtonsContainer marginRight>
          {buttonsGroup1.map((item, i) => (
            <ButtonsWrapper
              key={item.tag}
              lastButton={i === 4}
              firstButton={i === 0}
              data-cy={`article-button-${item.tag}`}
            >
              <Button href={item.url}>{item.tag}</Button>
            </ButtonsWrapper>
          ))}
        </ButtonsContainer>
        <ButtonsContainer marginRight>
          {buttonsGroup2.map((item, i) => (
            <ButtonsWrapper
              key={item.tag}
              lastButton={i === 4}
              firstButton={i === 0}
              data-cy={`article-button-${item.tag}`}
            >
              <Button href={item.url}>{item.tag}</Button>
            </ButtonsWrapper>
          ))}
        </ButtonsContainer>
        <ButtonsContainer marginRight>
          {buttonsGroup3.map((item, i) => (
            <ButtonsWrapper
              key={item.tag}
              lastButton={i === 4}
              firstButton={i === 0}
              data-cy={`article-button-${item.tag}`}
            >
              <Button href={item.url}>{item.tag}</Button>
            </ButtonsWrapper>
          ))}
        </ButtonsContainer>
        <ButtonsContainer>
          {buttonsGroup4.map((item, i) => (
            <ButtonsWrapper
              key={item.tag}
              lastButton={i === 4}
              firstButton={i === 0}
              data-cy={`article-button-${item.tag}`}
            >
              <Button href={item.url}>{item.tag}</Button>
            </ButtonsWrapper>
          ))}
        </ButtonsContainer>
      </ArticleContainer>
    </SectionContainer>
  );
};

ArticleSection.defaultProps = defaultProps;

export default ArticleSection;
